import { ref, onMounted, onUnmounted, computed } from 'vue'
import { dashboardApi, type Activity } from '@/services/dashboard'
import { useToastService } from '@/services/toast'

// Extended Activity type with read status
export interface ActivityWithReadStatus extends Activity {
  isRead: boolean
  isNew?: boolean // Flag for newly arrived activities
}

export interface ActivityPollingOptions {
  /** Polling interval in milliseconds (default: 30000 = 30 seconds) */
  interval?: number
  /** Whether to show toast notifications for new activities (default: true) */
  enableToasts?: boolean
  /** Whether to automatically start polling on mount (default: true) */
  autoStart?: boolean
  /** Maximum number of activities to keep in memory (default: 50) */
  maxActivities?: number
  /** Whether this is for admin view (affects notification messages) */
  isAdmin?: boolean
}

export function useActivityPolling(options: ActivityPollingOptions = {}) {
  const {
    interval = 5000, // Change the interval if needed
    enableToasts = true,
    autoStart = true,
    maxActivities = 50,
    isAdmin = false
  } = options

  const toast = useToastService()
  
  // State
  const activities = ref<ActivityWithReadStatus[]>([])
  const newActivitiesCount = ref(0)
  const unreadActivitiesCount = ref(0)
  const lastCheckedId = ref<number | string | null>(null)
  const isPolling = ref(false)
  const error = ref<string | null>(null)
  const loading = ref(false)
  const readActivityIds = ref<Set<number | string>>(new Set())
  const isInitialLoad = ref(true) // Track if this is the first load
  
  let pollingInterval: ReturnType<typeof setInterval> | null = null

  /**
   * Fetch activities from the API
   */
  const fetchActivities = async (silent = false) => {
    try {
      if (!silent) {
        loading.value = true
      }
      error.value = null

      const fetchedActivities = await dashboardApi.getRecentActivity()
      
      // Detect new activities (only if not initial load)
      if (activities.value.length > 0 && fetchedActivities.length > 0 && !isInitialLoad.value) {
        const currentNewestId = activities.value[0]?.id
        const newActivities: Activity[] = []
        
        for (const activity of fetchedActivities) {
          if (activity.id === currentNewestId) {
            break
          }
          newActivities.push(activity)
        }
        
        if (newActivities.length > 0) {
          newActivitiesCount.value += newActivities.length
          
          // Show toast notification for new activities
          if (enableToasts) {
            showNewActivityToast(newActivities)
          }
        }
      }
      
      // Convert fetched activities to ActivityWithReadStatus
      const activitiesWithReadStatus: ActivityWithReadStatus[] = fetchedActivities
        .slice(0, maxActivities)
        .map((activity) => {
          // Check if this activity was already in our list
          const existingActivity = activities.value.find(a => a.id === activity.id)
          
          if (existingActivity) {
            // Keep existing read status
            return {
              ...activity,
              isRead: existingActivity.isRead,
              isNew: false
            }
          } else if (isInitialLoad.value) {
            // Initial load - mark all as read (already seen)
            return {
              ...activity,
              isRead: true,
              isNew: false
            }
          } else {
            // New activity detected after initial load - mark as unread
            return {
              ...activity,
              isRead: false,
              isNew: true
            }
          }
        })
      
      // Update activities list
      activities.value = activitiesWithReadStatus
      
      // Mark initial load as complete
      if (isInitialLoad.value) {
        isInitialLoad.value = false
      }
      
      // Update unread count
      unreadActivitiesCount.value = activities.value.filter(a => !a.isRead).length
      
      // Update last checked ID
      if (fetchedActivities.length > 0) {
        lastCheckedId.value = fetchedActivities[0].id
      }
      
    } catch (err) {
      error.value = 'Failed to fetch activities'
      console.error('Activity polling error:', err)
    } finally {
      if (!silent) {
        loading.value = false
      }
    }
  }

  /**
   * Show toast notification for new activities
   */
  const showNewActivityToast = (newActivities: Activity[]) => {
    const count = newActivities.length
    
    if (count === 1) {
      const activity = newActivities[0]
      const message = getActivityNotificationMessage(activity)
      toast.info(message)
    } else {
      toast.info(
        isAdmin 
          ? `${count} new activities recorded`
          : `${count} new notifications`
      )
    }
  }

  /**
   * Get a user-friendly notification message for an activity
   */
  const getActivityNotificationMessage = (activity: Activity): string => {
    const { type, first_name, last_name } = activity
    const name = first_name && last_name ? `${first_name} ${last_name}` : 'Someone'
    
    switch (type) {
      case 'user_registration':
        return isAdmin ? `${name} registered as a new employee` : 'New employee registered'
      case 'module_assignment':
        return isAdmin ? `${name} was assigned training modules` : 'You have new training modules'
      case 'exam_result':
        return isAdmin 
          ? `${name} completed an exam` 
          : `Your exam result is available`
      case 'module_start':
        return isAdmin ? `${name} started a module` : 'Module started'
      case 'module_completion':
        return isAdmin ? `${name} completed a module` : 'Module completed'
      default:
        return isAdmin ? 'New activity recorded' : 'New notification'
    }
  }

  /**
   * Start polling for activities
   */
  const startPolling = () => {
    if (isPolling.value) {
      return
    }
    
    isPolling.value = true
    
    // Initial fetch
    fetchActivities()
    
    // Set up polling interval
    pollingInterval = setInterval(() => {
      fetchActivities(true) // Silent polling
    }, interval)
  }

  /**
   * Stop polling for activities
   */
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
    isPolling.value = false
  }

  /**
   * Clear the new activities count (mark as seen)
   */
  const clearNewActivitiesCount = () => {
    newActivitiesCount.value = 0
  }

  /**
   * Mark a specific activity as read
   */
  const markAsRead = (activityId: number | string) => {
    const activity = activities.value.find(a => a.id === activityId)
    if (activity && !activity.isRead) {
      activity.isRead = true
      activity.isNew = false
      readActivityIds.value.add(activityId)
      unreadActivitiesCount.value = activities.value.filter(a => !a.isRead).length
    }
  }

  /**
   * Mark a specific activity as unread
   */
  const markAsUnread = (activityId: number | string) => {
    const activity = activities.value.find(a => a.id === activityId)
    if (activity && activity.isRead) {
      activity.isRead = false
      readActivityIds.value.delete(activityId)
      unreadActivitiesCount.value = activities.value.filter(a => !a.isRead).length
    }
  }

  /**
   * Mark all activities as read
   */
  const markAllAsRead = () => {
    activities.value.forEach(activity => {
      activity.isRead = true
      activity.isNew = false
      readActivityIds.value.add(activity.id)
    })
    unreadActivitiesCount.value = 0
  }

  /**
   * Mark all activities as unread
   */
  const markAllAsUnread = () => {
    activities.value.forEach(activity => {
      activity.isRead = false
      readActivityIds.value.delete(activity.id)
    })
    unreadActivitiesCount.value = activities.value.length
  }

  /**
   * Toggle read status of an activity
   */
  const toggleReadStatus = (activityId: number | string) => {
    const activity = activities.value.find(a => a.id === activityId)
    if (activity) {
      if (activity.isRead) {
        markAsUnread(activityId)
      } else {
        markAsRead(activityId)
      }
    }
  }

  /**
   * Manually refresh activities
   */
  const refresh = async () => {
    await fetchActivities(false)
  }

  // Computed
  const hasNewActivities = computed(() => newActivitiesCount.value > 0)
  const hasUnreadActivities = computed(() => unreadActivitiesCount.value > 0)
  const readActivities = computed(() => activities.value.filter(a => a.isRead))
  const unreadActivities = computed(() => activities.value.filter(a => !a.isRead))
  const newActivities = computed(() => activities.value.filter(a => a.isNew))

  // Lifecycle
  onMounted(() => {
    if (autoStart) {
      startPolling()
    }
  })

  onUnmounted(() => {
    stopPolling()
  })

  return {
    // State
    activities,
    newActivitiesCount,
    unreadActivitiesCount,
    hasNewActivities,
    hasUnreadActivities,
    isPolling,
    loading,
    error,
    
    // Computed
    readActivities,
    unreadActivities,
    newActivities,
    
    // Methods
    startPolling,
    stopPolling,
    clearNewActivitiesCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    markAllAsUnread,
    toggleReadStatus,
    refresh
  }
}
